import React, { useState, useEffect } from 'react';
import BBALoader from '../BBALoader';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import clientRoadmapService from '../../services/clientRoadmapService';
import { transformClientRoadmapTemplates } from '../../utils/roadmapDashboardTasks';
import { getPillarEmoji } from '../../utils/portalEmojis';
import { resolvePortalPagePath } from '../../data/portalPageOptions';

const isTaskDone = (task) =>
  Boolean(task?.completed) ||
  ['done', 'completed'].includes(String(task?.status || '').toLowerCase());

/**
 * Department roadmap progress card. Reads the COMPANY roadmap
 * (accounts/{companyId}.roadmapTemplates) — the same source the full Roadmap page
 * and ClientDashboard use — filters to this department, and shows the current
 * step (its own emoji + name), how many steps are done, and the current task
 * (with its link/button if the task has one).
 */
const DepartmentRoadmap = ({ category, companyId, loading: companyLoading = false, className = '' }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyLoading) return undefined;
    if (!companyId) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const clientRoadmap = await clientRoadmapService.getClientRoadmap(companyId);
        const all = transformClientRoadmapTemplates(clientRoadmap);
        const deptItems = all.filter(
          (item) => String(item.department || '').toLowerCase() === String(category).toLowerCase()
        );
        if (!cancelled) setItems(deptItems);
      } catch (error) {
        console.error('Error loading department roadmap:', error);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, companyLoading, category]);

  const total = items.length;
  const doneCount = items.filter((item) => item.status === 'completed').length;
  const currentIndex = (() => {
    const inProgress = items.findIndex((item) => item.status === 'in-progress');
    if (inProgress !== -1) return inProgress;
    return items.findIndex((item) => item.status !== 'completed');
  })();
  const current = currentIndex !== -1 ? items[currentIndex] : null;
  const currentTask = current?.tasks?.find((task) => !isTaskDone(task)) || null;
  const stepNumber = current ? currentIndex + 1 : total;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const busy = loading || companyLoading;
  const emoji = current?.emoji || getPillarEmoji(category);

  const taskExternal = currentTask?.externalLink;
  // Roadmap tasks store portal pages with or without a leading slash, and some
  // point at pages that have since moved — resolve before navigating.
  const taskPortalPage = resolvePortalPagePath(currentTask?.portalPage);
  const hasTaskLink = Boolean(taskExternal || taskPortalPage);
  const openTaskLink = () => {
    if (taskExternal) {
      window.open(taskExternal, '_blank', 'noopener,noreferrer');
    } else if (taskPortalPage) {
      navigate(taskPortalPage);
    }
  };

  const cardClass = `rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm p-4 flex flex-col ${className}`;

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="text-base leading-none" aria-hidden="true">🗺️</span>
        <h2 className="font-semibold text-gray-800 dark:text-white">Roadmap</h2>
      </div>

      {busy ? (
        <div className="flex items-center justify-center flex-1 py-10">
          <BBALoader size={24} />
        </div>
      ) : total === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 text-center py-6">
          <span className="text-4xl mb-2" aria-hidden="true">{emoji}</span>
          <p className="text-sm text-gray-500 dark:text-gray-400">No roadmap steps yet.</p>
        </div>
      ) : (
        <div className="flex flex-col flex-1">
          {/* Current step — big emoji + step name */}
          <div className="text-center mb-3">
            <span className="block text-5xl leading-none mb-2" aria-hidden="true">{emoji}</span>
            <h3 className="font-bold text-lg text-gray-900 dark:text-white leading-tight line-clamp-2">
              {current ? current.title : 'All steps complete! 🎉'}
            </h3>
          </div>

          {/* Step progress */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                {current ? `Step ${stepNumber} of ${total}` : `${total} of ${total} steps`}
              </span>
              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                {doneCount}/{total} done
              </span>
            </div>
            <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {/* Current task + its link/button */}
          {currentTask && (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-2.5 text-left mt-auto">
              <p className="text-[10px] uppercase tracking-wide text-red-600 dark:text-red-400 font-semibold mb-0.5">
                Your current task
              </p>
              <p className="text-sm font-medium text-gray-800 dark:text-white line-clamp-2">
                {currentTask.title}
              </p>
              {currentTask.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                  {currentTask.description}
                </p>
              )}
              {hasTaskLink && (
                <button
                  type="button"
                  onClick={openTaskLink}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 hover:underline"
                >
                  {taskExternal ? 'Open link' : 'Go to page'}
                  {taskExternal ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/portal/roadmap')}
        className="mt-3 inline-flex items-center justify-center gap-1 text-sm font-medium text-red-600 hover:text-red-700 hover:underline"
      >
        View roadmap <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default DepartmentRoadmap;
